import type { ColumnDef } from "@tanstack/react-table";
import { Text } from "@mantine/core";
import { Money } from "@/components/Money";
import { Mono } from "@/components/Mono";
import { StatusBadge } from "@/components/StatusBadge";
import { Timestamp } from "@/components/Timestamp";
import { RelatedLink } from "@/components/RelatedLink";
import { humanize } from "@/lib/format";
import type { EntityKey } from "@/routes/paths";
import { col } from "./columns";

/** Plain humanized text column. */
export function textCol<T>(
  id: string,
  header: string,
  get: (r: T) => string | null | undefined,
  opts: { humanize?: boolean } = {},
): ColumnDef<T, unknown> {
  return col<T>({
    id,
    header,
    value: (r) => get(r) ?? "",
    cell: (r) => {
      const v = get(r);
      if (!v) return <Text c="dimmed">—</Text>;
      return <Text size="sm">{opts.humanize ? humanize(v) : v}</Text>;
    },
  });
}

/** Short monospace id/hash column. */
export function monoCol<T>(
  id: string,
  header: string,
  get: (r: T) => string | null | undefined,
  short = true,
): ColumnDef<T, unknown> {
  return col<T>({
    id,
    header,
    value: (r) => get(r) ?? "",
    cell: (r) => <Mono value={get(r)} short={short} copy={false} />,
  });
}

/** State badge column. */
export function stateCol<T>(
  get: (r: T) => string | null | undefined,
  header = "State",
): ColumnDef<T, unknown> {
  return col<T>({
    id: "state",
    header,
    value: (r) => get(r) ?? "",
    cell: (r) => <StatusBadge state={get(r)} />,
  });
}

/** Relative timestamp column. */
export function timeCol<T>(
  id: string,
  header: string,
  get: (r: T) => string | null | undefined,
): ColumnDef<T, unknown> {
  return col<T>({
    id,
    header,
    value: (r) => get(r) ?? "",
    cell: (r) => <Timestamp value={get(r)} />,
  });
}

/** microUSDC money column. */
export function moneyCol<T>(
  id: string,
  header: string,
  get: (r: T) => number | null | undefined,
): ColumnDef<T, unknown> {
  return col<T>({
    id,
    header,
    value: (r) => get(r) ?? 0,
    cell: (r) => <Money micro={get(r)} size="sm" />,
  });
}

/** Link to a related entity by its id. */
export function relatedCol<T>(
  id: string,
  header: string,
  entity: EntityKey,
  get: (r: T) => string | null | undefined,
): ColumnDef<T, unknown> {
  return col<T>({
    id,
    header,
    value: (r) => get(r) ?? "",
    cell: (r) => <RelatedLink entity={entity} id={get(r)} />,
  });
}
